import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {updateBackdropState, updateOffer} from 'Quoting/ducks/createQuote';
import {createEisTypeObject, isDefined} from 'Quoting/utils/utilities';
import {Map as ImmutableMap, fromJS} from 'immutable';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';

export const useBenefitToggle = () => {
  const dispatch = useDispatch();
  const {getQuoteFieldValue, updateQuoteFieldValues} = useQuoteField();
  const {selectedPlan, defaultPlanValues} = useSelector((state: ReducerState) => state.quoteForm);

  const planRootId = getQuoteFieldValue(`benefitPackagingDetail.plans[${selectedPlan?.planIndex}]._key.rootId`);
  const coverageDefId = getQuoteFieldValue(
    `benefitPackagingDetail.plans[${selectedPlan?.planIndex}].coverageDefs[0]._key.rootId`
  );

  const onToggleBenefitApply = async (
    benefitEntityName: string,
    benefitAppliedPath: string,
    value: boolean,
    benefitDetailsPath: string
  ) => {
    const fieldsToUpdate: {path: string, newValue: any}[] = [
      {
        path: benefitAppliedPath,
        newValue: value,
      },
    ];

    if (value && !isDefined(getQuoteFieldValue(benefitDetailsPath))) {
      const benefitDetails = getBenefitDetailsObject(
        benefitEntityName,
        defaultPlanValues?.get('GCI5'),
        planRootId ?? '',
        coverageDefId ?? ''
      );

      fieldsToUpdate.push({
        path: benefitDetailsPath,
        newValue: fromJS(benefitDetails),
      });
    }
    updateQuoteFieldValues(fieldsToUpdate);
    dispatch(updateBackdropState(true));
    await new Promise<void>(resolve => {
      dispatch(updateOffer(resolve));
    });
    dispatch(updateBackdropState(false));
  };

  const getBenefitDetailsObject = (
    benefitEntityName: string,
    defaultValues: ImmutableMap<string, any> | undefined,
    rootId: string,
    parentId: string
  ) => {
    switch (benefitEntityName) {
      case 'CISpecifiedChronicIllnessRider':
        return {
          planBenefitAmountPct: defaultValues.get('CISpecifiedChronicIllnessRider.planBenefitAmountPct'),
          isAdrenalInsufficiency: defaultValues.get('CISpecifiedChronicIllnessRider.isAdrenalInsufficiency'),
          isAmyotrophicLateralSclerosis: defaultValues.get(
            'CISpecifiedChronicIllnessRider.isAmyotrophicLateralSclerosis'
          ),
          isArthritis: defaultValues.get('CISpecifiedChronicIllnessRider.isArthritis'),
          isHuntingtonsChorea: defaultValues.get('CISpecifiedChronicIllnessRider.isHuntingtonsChorea'),
          isMultipleSclerosis: defaultValues.get('CISpecifiedChronicIllnessRider.isMultipleSclerosis'),
          isOsteomyelitis: defaultValues.get('CISpecifiedChronicIllnessRider.isOsteomyelitis'),
          isOsteoporosis: defaultValues.get('CISpecifiedChronicIllnessRider.isOsteoporosis'),
          isSCIMuscularDystrophy: defaultValues.get('CISpecifiedChronicIllnessRider.isSCIMuscularDystrophy'),
          exclusions: [
            {
              ...createEisTypeObject('CIExclSCIBenefitIntentionallySelfInflictedInjury', rootId, parentId),
              isExclusion: defaultValues.get(
                'CISpecifiedChronicIllnessRider.CIExclSCIBenefitIntentionallySelfInflictedInjury'
              ),
            },
            {
              ...createEisTypeObject('CIExclSCIBenefitAlcoholAbuse', rootId, parentId),
              isExclusion: defaultValues.get('CISpecifiedChronicIllnessRider.CIExclSCIBenefitAlcoholAbuse'),
            },
            {
              ...createEisTypeObject('CIExclSCIBenefitDrugAbuse', rootId, parentId),
              isExclusion: defaultValues.get('CISpecifiedChronicIllnessRider.CIExclSCIBenefitDrugAbuse'),
            },
            {
              ...createEisTypeObject('CIExclSCIBenefitDiagnosedBeforeEffectiveDate', rootId, parentId),
              isExclusion: defaultValues.get(
                'CISpecifiedChronicIllnessRider.CIExclSCIBenefitDiagnosedBeforeEffectiveDate'
              ),
            },
            {
              ...createEisTypeObject('CIExclSCIBenefitDiagnosedOutsideOfTheUS', rootId, parentId),
              isExclusion: defaultValues.get('CISpecifiedChronicIllnessRider.CIExclSCIBenefitDiagnosedOutsideOfTheUS'),
            },
            {
              ...createEisTypeObject('CIExclSCIBenefitPreExConditions', rootId, parentId),
              isExclusion: defaultValues.get('CISpecifiedChronicIllnessRider.CIExclSCIBenefitPreExConditions'),
            },
          ],
          ...createEisTypeObject('CISpecifiedChronicIllnessRider', rootId, parentId),
        };
      case 'CISpecifiedChronicIllnessAndAccidentalInjuryRider':
        return {
          injuryPct: defaultValues.get('CISpecifiedChronicIllnessAndAccidentalInjuryRider.injuryPct'),
          planBenefitAmountPct: defaultValues.get(
            'CISpecifiedChronicIllnessAndAccidentalInjuryRider.planBenefitAmountPct'
          ),
          isAdrenalInsufficiency: defaultValues.get(
            'CISpecifiedChronicIllnessAndAccidentalInjuryRider.isAdrenalInsufficiency'
          ),
          isAmyotrophicLateralSclerosis: defaultValues.get(
            'CISpecifiedChronicIllnessAndAccidentalInjuryRider.isAmyotrophicLateralSclerosis'
          ),
          isArthritis: defaultValues.get('CISpecifiedChronicIllnessAndAccidentalInjuryRider.isArthritis'),
          isHuntingtonsChorea: defaultValues.get(
            'CISpecifiedChronicIllnessAndAccidentalInjuryRider.isHuntingtonsChorea'
          ),
          isMultipleSclerosis: defaultValues.get(
            'CISpecifiedChronicIllnessAndAccidentalInjuryRider.isMultipleSclerosis'
          ),
          isOsteomyelitis: defaultValues.get('CISpecifiedChronicIllnessAndAccidentalInjuryRider.isOsteomyelitis'),
          isOsteoporosis: defaultValues.get('CISpecifiedChronicIllnessAndAccidentalInjuryRider.isOsteoporosis'),
          isSCIMuscularDystrophy: defaultValues.get(
            'CISpecifiedChronicIllnessAndAccidentalInjuryRider.isSCIMuscularDystrophy'
          ),
          exclusions: [
            {
              ...createEisTypeObject('CIExclSCIandAIBenefitIntentionallySelfInflictedInjury', rootId, parentId),
              isExclusion: defaultValues.get(
                'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitIntentionallySelfInflictedInjury'
              ),
            },
            {
              ...createEisTypeObject('CIExclSCIandAIBenefitAlcoholAbuse', rootId, parentId),
              isExclusion: defaultValues.get(
                'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitAlcoholAbuse'
              ),
            },
            {
              ...createEisTypeObject('CIExclSCIandAIBenefitDrugAbuse', rootId, parentId),
              isExclusion: defaultValues.get(
                'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitDrugAbuse'
              ),
            },
            {
              ...createEisTypeObject('CIExclSCIandAIBenefitDiagnosedBeforeEffectiveDate', rootId, parentId),
              isExclusion: defaultValues.get(
                'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitDiagnosedBeforeEffectiveDate'
              ),
            },
            {
              ...createEisTypeObject('CIExclSCIandAIBenefitDiagnosedOutsideOfTheUS', rootId, parentId),
              isExclusion: defaultValues.get(
                'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitDiagnosedOutsideOfTheUS'
              ),
            },
            {
              ...createEisTypeObject('CIExclSCIandAIBenefitPreExConditions', rootId, parentId),
              isExclusion: defaultValues.get(
                'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitPreExConditions'
              ),
            },
          ],
          ...createEisTypeObject('CISpecifiedChronicIllnessAndAccidentalInjuryRider', rootId, parentId),
        };
      default:
        return {};
    }
  };

  return {
    onToggleBenefitApply,
    getBenefitDetailsObject,
  };
};
